import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Card, PageHeader, StatCard } from '../components/ui';
import { useQaAssignments, useQaAssignmentsChart } from '../lib/hooks/useQaAssignments';

import styles from '../styles/common.module.css';

function formatWeekLabel(weekStr: string): string {
  const d = new Date(`${weekStr}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function Reviews() {
  const assignmentsQuery = useQaAssignments();
  const chartQuery = useQaAssignmentsChart();

  const assignments = assignmentsQuery.data ?? [];
  const chartData = (chartQuery.data ?? []).map((d) => ({ ...d, label: formatWeekLabel(d.week) }));

  const total = assignments.length;
  const totalCompleted = assignments.filter((a) => a.status === 'completed').length;
  const totalPending = assignments.filter((a) => a.status === 'pending').length;

  const byTse: Record<string, { completed: number; pending: number }> = {};
  const byReviewer: Record<string, { completed: number; pending: number }> = {};
  for (const a of assignments) {
    if (!byTse[a.tse_name]) byTse[a.tse_name] = { completed: 0, pending: 0 };
    if (!byReviewer[a.assigned_reviewer]) byReviewer[a.assigned_reviewer] = { completed: 0, pending: 0 };
    if (a.status === 'completed') {
      byTse[a.tse_name].completed++;
      byReviewer[a.assigned_reviewer].completed++;
    } else if (a.status === 'pending') {
      byTse[a.tse_name].pending++;
      byReviewer[a.assigned_reviewer].pending++;
    }
  }

  if (assignmentsQuery.isLoading || chartQuery.isLoading) {
    return <div className={styles.empty}>Loading…</div>;
  }

  return (
    <div className={styles.pageNarrow}>
      <PageHeader title="Reviews" />

      <div className={styles.statGrid}>
        <StatCard label="Total Assigned" value={total} />
        <StatCard label="Completed" value={totalCompleted} highlight />
        <StatCard label="Pending" value={totalPending} />
      </div>

      <Card>
        <h2 className={styles.sectionTitle}>Reviews Week Over Week</h2>
        {chartData.length === 0 ? (
          <div className={styles.empty}>No data yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--lp-color-border-ui-secondary)" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value, name) => [value as number, name === 'completed' ? 'Completed' : 'Pending']} />
              <Legend formatter={(n) => (n === 'completed' ? 'Completed' : 'Pending')} />
              <Bar dataKey="completed" stackId="a" fill="#4f46e5" />
              <Bar dataKey="pending" stackId="a" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))',
          gap: '1rem',
        }}
      >
        <Card>
          <h2 className={styles.sectionTitle}>By TSE</h2>
          {Object.entries(byTse).length === 0 ? (
            <div className={styles.empty}>No data.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Name</th>
                  <th className={styles.th} style={{ textAlign: 'right' }}>
                    Done
                  </th>
                  <th className={styles.th} style={{ textAlign: 'right' }}>
                    Pending
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(byTse).map(([name, counts]) => (
                  <tr key={name}>
                    <td className={styles.td} style={{ fontWeight: 500 }}>
                      {name}
                    </td>
                    <td className={styles.td} style={{ textAlign: 'right' }}>
                      {counts.completed}
                    </td>
                    <td className={styles.td} style={{ textAlign: 'right' }}>
                      {counts.pending}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card>
          <h2 className={styles.sectionTitle}>By Reviewer</h2>
          {Object.entries(byReviewer).length === 0 ? (
            <div className={styles.empty}>No data.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Reviewer</th>
                  <th className={styles.th} style={{ textAlign: 'right' }}>
                    Done
                  </th>
                  <th className={styles.th} style={{ textAlign: 'right' }}>
                    Pending
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(byReviewer)
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([name, counts]) => (
                    <tr key={name}>
                      <td className={styles.td} style={{ fontWeight: 500 }}>
                        {name}
                      </td>
                      <td className={styles.td} style={{ textAlign: 'right' }}>
                        {counts.completed}
                      </td>
                      <td className={styles.td} style={{ textAlign: 'right' }}>
                        {counts.pending}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
